import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getPartnerTasks } from '../services/fulfillmentService'
import type { FulfillmentTaskListItem } from '../types/fulfillment'

const POLL_MS = 45_000

function storageKey(uid: string) {
  return `ow_partner_new_order_notif_read:${uid}`
}

export function usePartnerNewOrderNotifications() {
  const { user, claims } = useAuth()
  const [tasks, setTasks] = useState<FulfillmentTaskListItem[]>([])
  const [readTaskIds, setReadTaskIds] = useState<Set<string>>(() => new Set())
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!user?.uid) {
      setReadTaskIds(new Set())
      return
    }
    try {
      const raw = localStorage.getItem(storageKey(user.uid))
      setReadTaskIds(new Set(raw ? (JSON.parse(raw) as string[]) : []))
    } catch {
      setReadTaskIds(new Set())
    }
  }, [user?.uid])

  const persistReadIds = useCallback(
    (next: Set<string>) => {
      if (!user?.uid) return
      localStorage.setItem(storageKey(user.uid), JSON.stringify([...next]))
      setReadTaskIds(next)
    },
    [user?.uid]
  )

  const fetchTasks = useCallback(async () => {
    if (!user || claims?.role !== 'partner') return
    setLoading(true)
    try {
      const { tasks: next } = await getPartnerTasks()
      setTasks(next)
    } catch {
      // Mantém lista anterior; a navbar não precisa exibir erro de rede.
    } finally {
      setLoading(false)
    }
  }, [user, claims?.role])

  useEffect(() => {
    if (!user || claims?.role !== 'partner') return
    fetchTasks()
    const id = window.setInterval(fetchTasks, POLL_MS)
    return () => window.clearInterval(id)
  }, [user, claims?.role, fetchTasks])

  const unreadNewOrders = useMemo(() => {
    const list = tasks.filter((t) => t.status === 'new' && !readTaskIds.has(t.id))
    list.sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return tb - ta
    })
    return list
  }, [tasks, readTaskIds])

  const markAsRead = useCallback(
    (taskId: string) => {
      const next = new Set(readTaskIds)
      next.add(taskId)
      persistReadIds(next)
    },
    [readTaskIds, persistReadIds]
  )

  const markAllNewOrdersAsRead = useCallback(() => {
    const next = new Set(readTaskIds)
    tasks.filter((t) => t.status === 'new').forEach((t) => next.add(t.id))
    persistReadIds(next)
  }, [tasks, readTaskIds, persistReadIds])

  return {
    unreadNewOrders,
    markAsRead,
    markAllNewOrdersAsRead,
    refetch: fetchTasks,
    loading,
  }
}
